import { Plus, X, GripVertical } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FIELD_CLASS, LABEL_CLASS } from "./shared";

// Drag handle wrapper for a single option GROUP card — reorders the whole
// group (label/required/choices) among other groups. Render-prop `children`
// gets the drag attributes/listeners so the caller can put the grip handle
// exactly where it wants inside the card header.
const SortableOptionGroup = ({ id, gi, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`admin-option-group-${gi}`} className="border border-[var(--border-default)] bg-[var(--bg-secondary)]/40 p-4 space-y-3">
      {children({ attributes, listeners })}
    </div>
  );
};

// Drag handle wrapper for a single CHOICE row within one option group —
// reorders choices among each other, scoped to that group's own list.
const SortableOptionChoice = ({ id, gi, ci, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`admin-option-choice-${gi}-${ci}`} className="flex items-center gap-2 pl-3">
      {children({ attributes, listeners })}
    </div>
  );
};

// Purchase options (variants with different price/stock) — option groups with drag-to-reorder
// choices, plus the per-combination stock override matrix. Only shown for existing products.
// `helpText`/`groupPlaceholder`/`comboHelpText`/`showRequiredToggle` are optional overrides so
// this can be reused by editors (like Wellside Canopies) whose copy/data shape differs slightly.
export const OptionGroupsSection = ({
  isNew, form, optionGroupSensors, combinations, findComboEntry, toggleComboStock,
  addOptionGroup, removeOptionGroup, updateOptionGroup, addChoice, removeChoice, updateChoice,
  handleOptionGroupDragEnd, handleChoiceDragEnd,
  helpText, groupPlaceholder = "Group label, e.g. Fitment", comboHelpText, showRequiredToggle = true,
}) => {
  if (isNew) return null;
  return (
    <div>
      <span className={LABEL_CLASS}>Purchase Options (variants with different price/stock)</span>
      <p className="text-white/40 text-xs mt-1 mb-3">
        {helpText || (
          <>
            Only needed if this product has multiple purchasable configurations at different prices/stock — e.g. a
            group called "Fitment" with choices like "Roof Rack Mount", each with its own price add-on and its own
            In Stock toggle. Leave empty for a simple single-price product. When groups exist, customers must pick a
            choice before Buy Now activates, and out-of-stock choices can't be selected.
          </>
        )}
      </p>
      <div className="space-y-4">
        <DndContext sensors={optionGroupSensors} collisionDetection={closestCenter} onDragEnd={handleOptionGroupDragEnd}>
          <SortableContext items={form.optionGroups.map((g, i) => `optgroup-${i}`)} strategy={verticalListSortingStrategy}>
            {form.optionGroups.map((group, gi) => {
              const groupId = `optgroup-${gi}`;
              return (
                <SortableOptionGroup key={groupId} id={groupId} gi={gi}>
                  {({ attributes, listeners }) => (
                    <>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          {...attributes}
                          {...listeners}
                          data-testid={`admin-option-group-drag-${gi}`}
                          title="Drag to reorder this option group"
                          className="text-white/40 hover:text-[var(--brand-primary)] cursor-grab touch-none shrink-0"
                        >
                          <GripVertical size={16} />
                        </button>
                        <input
                          data-testid={`admin-option-group-label-${gi}`}
                          value={group.label}
                          onChange={(e) => updateOptionGroup(gi, "label", e.target.value)}
                          className={FIELD_CLASS + " mt-0 flex-1"}
                          placeholder={groupPlaceholder}
                        />
                        {showRequiredToggle && (
                          <label className="flex items-center gap-1.5 text-xs text-white/60 whitespace-nowrap">
                            <input data-testid={`admin-option-group-required-${gi}`} type="checkbox" checked={group.required} onChange={(e) => updateOptionGroup(gi, "required", e.target.checked)} />
                            Required
                          </label>
                        )}
                        <button type="button" data-testid={`admin-option-group-remove-${gi}`} onClick={() => removeOptionGroup(gi)} className="text-white/50 hover:text-red-400 shrink-0">
                          <X size={16} />
                        </button>
                      </div>
                      <div className="space-y-2 pl-2 border-l border-[var(--border-default)]">
                        <DndContext sensors={optionGroupSensors} collisionDetection={closestCenter} onDragEnd={handleChoiceDragEnd(gi)}>
                          <SortableContext items={group.choices.map((c, i) => `optchoice-${gi}-${i}`)} strategy={verticalListSortingStrategy}>
                            {group.choices.map((choice, ci) => {
                              const choiceId = `optchoice-${gi}-${ci}`;
                              return (
                                <SortableOptionChoice key={choiceId} id={choiceId} gi={gi} ci={ci}>
                                  {({ attributes: cAttrs, listeners: cListeners }) => (
                                    <>
                                      <button
                                        type="button"
                                        {...cAttrs}
                                        {...cListeners}
                                        data-testid={`admin-option-choice-drag-${gi}-${ci}`}
                                        title="Drag to reorder this choice"
                                        className="text-white/40 hover:text-[var(--brand-primary)] cursor-grab touch-none shrink-0"
                                      >
                                        <GripVertical size={14} />
                                      </button>
                                      <input
                                        data-testid={`admin-option-choice-label-${gi}-${ci}`}
                                        value={choice.label}
                                        onChange={(e) => updateChoice(gi, ci, "label", e.target.value)}
                                        className={FIELD_CLASS + " mt-0 flex-1"}
                                        placeholder="Choice label"
                                      />
                                      <input
                                        data-testid={`admin-option-choice-price-${gi}-${ci}`}
                                        type="number"
                                        step="0.01"
                                        value={choice.priceDelta}
                                        onChange={(e) => updateChoice(gi, ci, "priceDelta", Number(e.target.value))}
                                        className={FIELD_CLASS + " mt-0 !w-28 flex-none"}
                                        placeholder="+$ price"
                                      />
                                      <label className="flex items-center gap-1.5 text-xs text-white/60 whitespace-nowrap">
                                        <input data-testid={`admin-option-choice-in-stock-${gi}-${ci}`} type="checkbox" checked={choice.inStock !== false} onChange={(e) => updateChoice(gi, ci, "inStock", e.target.checked)} />
                                        In Stock
                                      </label>
                                      <button type="button" data-testid={`admin-option-choice-remove-${gi}-${ci}`} onClick={() => removeChoice(gi, ci)} className="text-white/50 hover:text-red-400 shrink-0">
                                        <X size={14} />
                                      </button>
                                    </>
                                  )}
                                </SortableOptionChoice>
                              );
                            })}
                          </SortableContext>
                        </DndContext>
                        <button type="button" data-testid={`admin-option-choice-add-${gi}`} onClick={() => addChoice(gi)} className="pl-3 text-xs font-mono text-white/50 hover:text-[var(--brand-primary)] underline">
                          + Add Choice
                        </button>
                      </div>
                    </>
                  )}
                </SortableOptionGroup>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <p className="text-white/40 text-xs mt-2">Drag the grip handle on a group or choice to reorder how it appears to customers on the product page.</p>
      <button type="button" data-testid="admin-option-group-add" onClick={addOptionGroup} className="btn-secondary mt-3">
        <Plus size={14} /> Add Option Group
      </button>

      {combinations.length > 0 && form.optionGroups.filter((g) => g.choices.length > 0).length >= 2 && (
        <div className="mt-6">
          <span className={LABEL_CLASS}>Stock by Combination</span>
          <p className="text-white/40 text-xs mt-1 mb-3">
            {comboHelpText || (
              <>
                You have multiple option groups — some combinations might not be in stock independently of others.
                Untick any combination that customers should NOT be able to buy right now — they'll see a warning and
                Buy Now will be blocked for that specific combination, even though the individual choices stay selectable.
              </>
            )}
          </p>
          <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
            {combinations.map((combo, i) => {
              const entry = findComboEntry(combo);
              const checked = !entry || entry.inStock !== false;
              return (
                <label key={i} data-testid={`admin-combo-${i}`} className="flex items-center gap-3 px-3 py-2 text-sm text-white/80">
                  <input
                    data-testid={`admin-combo-checkbox-${i}`}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleComboStock(combo, e.target.checked)}
                  />
                  {Object.values(combo).join(" + ")}
                  {!checked && <span className="text-red-400 text-xs font-mono ml-auto">OUT OF STOCK</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
